import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { HERO_IMAGES } from "../../data/mockData";
import { cn } from "../../lib/utils";

const Hero = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, fade: true }, [Autoplay({ delay: 5000 })]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-slate-900">
      {/* Carousel */}
      <div className="absolute inset-0 z-0" ref={emblaRef}>
        <div className="flex h-full">
            {HERO_IMAGES.map((src, index) => (
                <div className="flex-[0_0_100%] min-w-0 relative h-full" key={index}>
                    <div 
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[10000ms] ease-in-out scale-110"
                        style={{
                            backgroundImage: `url('${src}')`,
                            transform: index === selectedIndex ? "scale(1.05)" : "scale(1.15)"
                        }}
                    ></div>
                    {/* Darker overlay for better text readability */}
                    <div className="absolute inset-0 bg-slate-900/50"></div>
                </div>
            ))}
        </div>
      </div>
      
      {/* Overlay Gradient */}
      <div className="absolute inset-0 hero-overlay pointer-events-none z-0"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-center">
        <div className="max-w-4xl space-y-6 animate-fade-in-up">
          <div className="flex items-center gap-4">
            <div className="h-1 w-20 bg-gold"></div>
            <span className="text-gold font-display font-bold uppercase tracking-widest text-sm bg-white/10 backdrop-blur-sm px-3 py-1">
              Predict. Prevent. Perform.
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight uppercase shadow-sm">
            Integrated Industrial Solutions for <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
              Mining &amp; Metals
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl font-light leading-relaxed border-l-4 border-gold pl-6 drop-shadow-md">
            From Greenfield Development to Brownfield Modernization, Predictive Maintenance, and Regulatory Compliance — we deliver reliability at every stage of the industrial asset lifecycle.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-8">
            <Button data-testid="hero-consultation-btn" size="lg" className="bg-gold hover:bg-gold/90 text-slate-900 rounded-none px-8 py-6 font-display uppercase tracking-widest text-sm font-bold" asChild>
              <Link to="/contact">
                Request Consultation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button data-testid="hero-services-btn" size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black rounded-none px-8 py-6 font-display uppercase tracking-widest text-sm bg-transparent" asChild>
              <Link to="/services">Explore Services</Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Carousel Indicators */}
      <div className="absolute bottom-10 right-10 z-20 flex gap-2">
         {HERO_IMAGES.map((_, index) => (
             <button
                key={index}
                className={cn("h-1 transition-all duration-300 rounded-full", index === selectedIndex ? "w-8 bg-gold" : "w-4 bg-white/50 hover:bg-white")}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
             />
         ))}
      </div>
    </div>
  );
};

export default Hero;
